"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.P2PCommunicationClient = void 0;
const sodium = require("libsodium-wrappers");
const bignumber_js_1 = require("bignumber.js");
const crypto_1 = require("./utils/crypto");
const MatrixClient_1 = require("./matrix-client/MatrixClient");
const MatrixClientEvent_1 = require("./matrix-client/models/MatrixClientEvent");
const MatrixMessage_1 = require("./matrix-client/models/MatrixMessage");
class P2PCommunicationClient {
    constructor(name, keyPair, replicationCount, debug = false) {
        this.name = name;
        this.keyPair = keyPair;
        this.replicationCount = replicationCount;
        this.debug = debug;
        this.clients = [];
        this.KNOWN_RELAY_SERVERS = [
            'matrix.papers.tech'
            // 'matrix.tez.ie',
            // 'matrix-dev.papers.tech',
            // "matrix.stove-labs.com",
            // "yadayada.cryptonomic-infra.tech"
        ];
        this.activeListeners = new Map();
    }
    async getHandshakeInfo() {
        return {
            name: this.name,
            publicKey: await this.getPublicKey(),
            relayServer: await this.getRelayServer()
        };
    }
    async getRelayServer(publicKeyHash, nonce = '') {
        if (!this.keyPair) {
            throw new Error('KeyPair not available');
        }
        const hash = publicKeyHash || (await crypto_1.getHexHash(this.keyPair.publicKey));
        return this.KNOWN_RELAY_SERVERS.reduce(async (prevPromise, curr) => {
            const prev = await prevPromise;
            const prevRelayServerHash = await crypto_1.getHexHash(prev + nonce);
            const currRelayServerHash = await crypto_1.getHexHash(curr + nonce);
            const prevBigInt = await this.getAbsoluteBigIntDifference(hash, prevRelayServerHash);
            const currBigInt = await this.getAbsoluteBigIntDifference(hash, currRelayServerHash);
            return prevBigInt.isLessThan(currBigInt) ? prev : curr;
        }, Promise.resolve(this.KNOWN_RELAY_SERVERS[0]));
    }
    async start() {
        await this.log('starting client');
        await sodium.ready;
        const loginRawDigest = sodium.crypto_generichash(32, sodium.from_string(`login:${Math.floor(Date.now() / 1000 / (5 * 60))}`));
        const rawSignature = sodium.crypto_sign_detached(loginRawDigest, this.keyPair.privateKey);
        await this.log(`connecting to ${this.replicationCount} servers`);
        for (let i = 0; i < this.replicationCount; i++) {
            // TODO: Parallel
            const client = MatrixClient_1.MatrixClient.create({
                baseUrl: `https://${await this.getRelayServer(await this.getPublicKeyHash(), i.toString())}`
            });
            client.subscribe(MatrixClientEvent_1.MatrixClientEventType.INVITE, async (event) => {
                await client.joinRooms(event.content.roomId);
            });
            await this.log('login', await this.getPublicKeyHash(), 'on', await this.getRelayServer(await this.getPublicKeyHash(), i.toString()));
            await client
                .start({
                id: await this.getPublicKeyHash(),
                password: `ed:${crypto_1.toHex(rawSignature)}:${await this.getPublicKey()}`,
                deviceId: crypto_1.toHex(this.keyPair.publicKey)
            })
                .catch((error) => this.log(error));
            await client.joinRooms(...client.invitedRooms).catch((error) => this.log(error));
            this.clients.push(client);
        }
    }
    async listenForEncryptedMessage(senderPublicKey, messageCallback) {
        if (!this.keyPair) {
            throw new Error('KeyPair not available');
        }
        const { sharedRx } = await this.createCryptoBoxServer(senderPublicKey, this.keyPair.privateKey);
        if (this.activeListeners.has(senderPublicKey)) {
            return;
        }
        const callbackFunction = async (event) => {
            if (this.isTextMessage(event.content) && (await this.isSender(event, senderPublicKey))) {
                const payload = Buffer.from(event.content.message.content, 'hex');
                if (payload.length >=
                    sodium.crypto_secretbox_NONCEBYTES + sodium.crypto_secretbox_MACBYTES) {
                    try {
                        messageCallback(await crypto_1.decryptCryptoboxPayload(payload, sharedRx));
                    }
                    catch (decryptionError) {
                        /* NO-OP. We try to decode every message, but some might not be addressed to us. */
                    }
                }
            }
        };
        this.activeListeners.set(senderPublicKey, callbackFunction);
        for (const client of this.clients) {
            client.subscribe(MatrixClientEvent_1.MatrixClientEventType.MESSAGE, callbackFunction);
        }
    }
    async unsubscribeFromEncryptedMessage(senderPublicKey) {
        const listener = this.activeListeners.get(senderPublicKey);
        if (!listener) {
            return;
        }
        for (const client of this.clients) {
            client.unsubscribe(MatrixClientEvent_1.MatrixClientEventType.MESSAGE, listener);
        }
        this.activeListeners.delete(senderPublicKey);
    }
    async unsubscribeFromEncryptedMessages() {
        for (const client of this.clients) {
            client.unsubscribe(MatrixClientEvent_1.MatrixClientEventType.MESSAGE);
        }
        this.activeListeners.clear();
    }
    async sendMessage(recipientPublicKey, message) {
        if (!this.keyPair) {
            throw new Error('KeyPair not available');
        }
        const { sharedTx } = await this.createCryptoBoxClient(recipientPublicKey, this.keyPair.privateKey);
        for (let i = 0; i < this.replicationCount; i++) {
            const recipientHash = await crypto_1.getHexHash(Buffer.from(recipientPublicKey, 'hex'));
            const recipient = crypto_1.recipientString(recipientHash, await this.getRelayServer(recipientHash, i.toString()));
            for (const client of this.clients) {
                const room = await this.getRelevantRoom(client, recipient);
                client
                    .sendTextMessage(room.id, await crypto_1.encryptCryptoboxPayload(message, sharedTx))
                    .catch((error) => this.log(error));
            }
        }
    }
    async listenForChannelOpening(messageCallback) {
        for (const client of this.clients) {
            client.subscribe(MatrixClientEvent_1.MatrixClientEventType.MESSAGE, async (event) => {
                await this.log('channel opening', event);
                if (this.isTextMessage(event.content) && (await this.isChannelOpenMessage(event.content))) {
                    if (!this.keyPair) {
                        throw new Error('KeyPair not available');
                    }
                    await this.log('new channel open event!');
                    const splits = event.content.message.content.split(':');
                    const payload = Buffer.from(splits[splits.length - 1], 'hex');
                    if (payload.length >=
                        sodium.crypto_secretbox_NONCEBYTES + sodium.crypto_secretbox_MACBYTES) {
                        try {
                            messageCallback(await crypto_1.openCryptobox(payload, this.keyPair.publicKey, this.keyPair.privateKey));
                        }
                        catch (decryptionError) {
                            /* NO-OP. We try to decode every message, but some might not be addressed to us. */
                        }
                    }
                }
            });
        }
    }
    async openChannel(recipientPublicKey, relayServer) {
        await this.log('open channel');
        const recipientHash = await crypto_1.getHexHash(Buffer.from(recipientPublicKey, 'hex'));
        const recipient = crypto_1.recipientString(recipientHash, relayServer);
        await this.log(`currently there are ${this.clients.length} clients open`);
        for (const client of this.clients) {
            const room = await this.getRelevantRoom(client, recipient);
            const encryptedMessage = await crypto_1.sealCryptobox(await this.getPublicKey(), Buffer.from(recipientPublicKey, 'hex'));
            client
                .sendTextMessage(room.id, ['@channel-open', recipient, encryptedMessage].join(':'))
                .catch((error) => this.log(error));
        }
    }
    isTextMessage(content) {
        return content.message.type === MatrixMessage_1.MatrixMessageType.TEXT;
    }
    async isChannelOpenMessage(content) {
        return content.message.content.startsWith(`@channel-open:@${await crypto_1.getHexHash(Buffer.from(await this.getPublicKey(), 'hex'))}`);
    }
    async isSender(event, senderPublicKey) {
        return event.content.message.sender.startsWith(`@${await crypto_1.getHexHash(Buffer.from(senderPublicKey, 'hex'))}`);
    }
    async getPublicKey() {
        if (!this.keyPair) {
            throw new Error('KeyPair not available');
        }
        return crypto_1.toHex(this.keyPair.publicKey);
    }
    async getPublicKeyHash() {
        if (!this.keyPair) {
            throw new Error('KeyPair not available');
        }
        return crypto_1.getHexHash(this.keyPair.publicKey);
    }
    async getAbsoluteBigIntDifference(firstHash, secondHash) {
        const difference = new bignumber_js_1.default(`0x${firstHash}`).minus(`0x${secondHash}`);
        return difference.absoluteValue();
    }
    async createCryptoBox(otherPublicKey, selfPrivateKey) {
        // TODO: Don't calculate it every time?
        const kxSelfPrivateKey = sodium.crypto_sign_ed25519_sk_to_curve25519(Buffer.from(selfPrivateKey)); // Secret bytes to scalar bytes
        const kxSelfPublicKey = sodium.crypto_sign_ed25519_pk_to_curve25519(Buffer.from(selfPrivateKey).slice(32, 64)); // Secret bytes to scalar bytes
        const kxOtherPublicKey = sodium.crypto_sign_ed25519_pk_to_curve25519(Buffer.from(otherPublicKey, 'hex')); // Secret bytes to scalar bytes
        return [
            Buffer.from(kxSelfPublicKey),
            Buffer.from(kxSelfPrivateKey),
            Buffer.from(kxOtherPublicKey)
        ];
    }
    async createCryptoBoxServer(otherPublicKey, selfPrivateKey) {
        const keys = await this.createCryptoBox(otherPublicKey, selfPrivateKey);
        return sodium.crypto_kx_server_session_keys(...keys);
    }
    async createCryptoBoxClient(otherPublicKey, selfPrivateKey) {
        const keys = await this.createCryptoBox(otherPublicKey, selfPrivateKey);
        return sodium.crypto_kx_client_session_keys(...keys);
    }
    async getRelevantRoom(client, recipient) {
        const joinedRooms = client.joinedRooms;
        const relevantRooms = joinedRooms.filter((roomElement) => roomElement.members.some((member) => member === recipient));
        let room;
        if (relevantRooms.length === 0) {
            await this.log(`no relevant rooms found`);
            const roomId = await client.createTrustedPrivateRoom(recipient);
            room = client.getRoomById(roomId);
        }
        else {
            room = relevantRooms[0];
            await this.log(`channel already open, reusing room ${room.id}`);
        }
        return room;
    }
    async log(...args) {
        if (this.debug) {
            console.log(`--- [P2PCommunicationClient]:${this.name}: `, ...args);
        }
    }
}
exports.P2PCommunicationClient = P2PCommunicationClient;
//# sourceMappingURL=P2PCommunicationClient.js.map